import { useState, useEffect, useCallback } from 'react'
import { useDashboardService } from 'hooks/useDashboardService'
import { useAuth } from 'hooks/useAuth'

const DEFAULT_CONFIG = {
  kpis: [
    { type: 'cadaveres_dia', sectorId: null, sectorName: null },
    { type: 'exames_dia', sectorId: null, sectorName: null },
    { type: 'vestigios_dia', sectorId: null, sectorName: null },
  ],
  charts: [
    { type: 'cadaveres_mes', sectorId: null, sectorName: null },
    { type: 'exames_atribuidos_mes', sectorId: null, sectorName: null },
  ],
  tables: [
    { type: 'ultimos_cadaveres', sectorId: null, sectorName: null },
  ],
}

export const useDashboard = () => {
  const dashboardService = useDashboardService()
  const { currentUser } = useAuth()

  const [config, setConfig] = useState(DEFAULT_CONFIG)

  const [kpiData, setKpiData] = useState([null, null, null])
  const [chartData, setChartData] = useState([[], []])
  const [tableData, setTableData] = useState([])

  const [processingKpis, setProcessingKpis] = useState([false, false, false])
  const [processingCharts, setProcessingCharts] = useState([false, false])
  const [processingTable, setProcessingTable] = useState(false)


  const fetchKpiCell = useCallback(async (index, cellConfig) => {
    if (!cellConfig) return


    setKpiData((prev) => {
      const copy = [...prev]
      copy[index] = null
      return copy
    })

    const setCellProcessing = (val) => {
      setProcessingKpis((prev) => {
        const copy = [...prev]
        copy[index] = val
        return copy
      })
    }

    let result = null
    const { type, sectorId } = cellConfig

    if (type === 'cadaveres_dia') {
      result = await dashboardService.getCadaveresDiaAsync(setCellProcessing)
    } else if (type === 'exames_dia') {
      result = await dashboardService.getExamesDiaAsync(setCellProcessing)
    } else if (type === 'vestigios_dia') {
      result = await dashboardService.getVestigiosDiaAsync(setCellProcessing)
    } else if (type === 'vestigios_setor' && sectorId) {
      result = await dashboardService.getVestigiosSetorAsync(sectorId, setCellProcessing)
    } else if (type === 'vestigios_usuario') {
      result = await dashboardService.getVestigiosUsuarioAsync(currentUser?.id, setCellProcessing)
    } else if (type === 'exames_atribuidos') {
      result = await dashboardService.getExamesAtribuidosUsuarioAsync(currentUser?.id, setCellProcessing)
    } else if (type === 'exames_solicitados_setor' && sectorId) {
      result = await dashboardService.getExamesSolicitadosSetorAsync(sectorId, setCellProcessing)
    }

    setKpiData((prev) => {
      const copy = [...prev]
      copy[index] = result
      return copy
    })
  }, [currentUser])


  const fetchChartCell = useCallback(async (index, cellConfig) => {
    if (!cellConfig) return


    setChartData((prev) => {
      const copy = [...prev]
      copy[index] = []
      return copy
    })

    const setCellProcessing = (val) => {
      setProcessingCharts((prev) => {
        const copy = [...prev]
        copy[index] = val
        return copy
      })
    }

    let result = []
    const { type, sectorId } = cellConfig

    if (type === 'cadaveres_mes') {
      result = await dashboardService.getCadaveresMesAsync(setCellProcessing)
    } else if (type === 'exames_mes') {
      result = await dashboardService.getExamesMesAsync(setCellProcessing)
    } else if (type === 'vestigios_setor_mes' && sectorId) {
      result = await dashboardService.getVestigiosRecebidosSetorMesAsync(sectorId, setCellProcessing)
    } else if (type === 'exames_setor_mes' && sectorId) {
      result = await dashboardService.getExamesSolicitadosSetorMesAsync(sectorId, setCellProcessing)
    } else if (type === 'exames_atribuidos_mes') {
      result = await dashboardService.getExamesAtribuidosUsuarioMesAsync(currentUser?.id, setCellProcessing)
    } else if (type === 'exames_publicados_mes') {
      result = await dashboardService.getExamesFinalizadosUsuarioMesAsync(currentUser?.id, setCellProcessing)
    }

    setChartData((prev) => {
      const copy = [...prev]
      copy[index] = result
      return copy
    })
  }, [currentUser])


  const fetchTableCell = useCallback(async (cellConfig) => {
    if (!cellConfig) return

    setTableData([])

    let result = []
    const { type } = cellConfig

    if (type === 'ultimos_cadaveres') {
      result = await dashboardService.getLatestCadaversAsync(setProcessingTable)
    } else if (type === 'ultimos_exames_atribuidos') {
      result = await dashboardService.getLatestExamsAssignedAsync(currentUser?.id, setProcessingTable)
    } else if (type === 'ultimos_exames_solicitados') {
      result = await dashboardService.getLatestExamsRequestedAsync(currentUser?.id, setProcessingTable)
    }

    setTableData(result)
  }, [currentUser])

  useEffect(() => {
    if (!currentUser?.id) return

    const storageKey = `sigep:dashboard_config:${currentUser.id}`
    const savedConfig = localStorage.getItem(storageKey)
    let activeConfig = DEFAULT_CONFIG

    if (savedConfig) {
      try {
        activeConfig = JSON.parse(savedConfig)
      } catch (e) {
        activeConfig = DEFAULT_CONFIG
      }
    }
    setConfig(activeConfig)


    activeConfig.kpis.forEach((cell, idx) => fetchKpiCell(idx, cell))
    activeConfig.charts.forEach((cell, idx) => fetchChartCell(idx, cell))
    if (activeConfig.tables && activeConfig.tables[0]) {
      fetchTableCell(activeConfig.tables[0])
    }
  }, [currentUser, fetchKpiCell, fetchChartCell, fetchTableCell])


  const updateCellConfig = useCallback((section, index, newCellConfig) => {
    if (!currentUser?.id) return

    setConfig((prevConfig) => {
      const updated = { ...prevConfig }
      if (section === 'kpis') {
        updated.kpis = [...updated.kpis]
        updated.kpis[index] = newCellConfig
      } else if (section === 'charts') {
        updated.charts = [...updated.charts]
        updated.charts[index] = newCellConfig
      } else if (section === 'tables') {
        updated.tables = [...updated.tables]
        updated.tables[index] = newCellConfig
      }

      const storageKey = `sigep:dashboard_config:${currentUser.id}`
      localStorage.setItem(storageKey, JSON.stringify(updated))
      return updated
    })


    if (section === 'kpis') {
      fetchKpiCell(index, newCellConfig)
    } else if (section === 'charts') {
      fetchChartCell(index, newCellConfig)
    } else if (section === 'tables') {
      fetchTableCell(newCellConfig)
    }
  }, [currentUser, fetchKpiCell, fetchChartCell, fetchTableCell])

  return {
    config,
    kpiData,
    chartData,
    tableData,
    processingKpis,
    processingCharts,
    processingTable,
    updateCellConfig,
  }
}
